import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectFacilityId } from 'src/store/configSlice';
import { DeviceAllocation, useGetLatestDeviceActivityQuery } from 'src/store/sageAdminApi';
import { getDevicesFromAllocations } from 'src/utils/getDevicesFromAllocation';

function useDeviceBatteryLevels(props: { deviceAllocations?: DeviceAllocation[] }) {
    const { deviceAllocations } = props;
    const facilityId = useSelector(selectFacilityId);
    const { devices } = getDevicesFromAllocations(deviceAllocations || []);

    // Extract device IDs from device allocations
    const deviceIds = useMemo(
        () => devices?.map((device) => device?.id).filter((id) => id !== undefined) || [],
        [devices]
    );

    // Get latest device activity for battery levels
    const { data: deviceActivityData, isLoading: isLoadingBatteryLevels } =
        useGetLatestDeviceActivityQuery(
            { getLatestDeviceActivityRequest: { facilityRid: facilityId, deviceRids: deviceIds } },
            { skip: !facilityId || !deviceIds.length }
        );

    // Create a Map of device IDs to battery levels
    const deviceBatteryLevelsMap = useMemo(() => {
        const batteryMap = new Map<string, number>();

        if (!deviceActivityData) {
            return batteryMap;
        }

        Object.entries(deviceActivityData).forEach(([deviceId, deviceActivity]) => {
            if (typeof deviceActivity === 'object' && deviceActivity?.batteryLevel !== undefined) {
                const batteryLevel = deviceActivity.batteryLevel;

                batteryMap.set(deviceId, batteryLevel);
            }
        });

        return batteryMap;
    }, [deviceActivityData]);

    return {
        deviceBatteryLevelsMap,
        isLoadingBatteryLevels,
    };
}

export default useDeviceBatteryLevels;
